// Chip and StatusChip primitives.

import type { ReactNode, MouseEventHandler } from "react";
import { cn } from "@/lib/cn";
import type { RecordStatus } from "@/lib/types";

export type ChipTone = "default" | "primary" | "secondary" | "tertiary" | "error";
export type ChipSize = "sm" | "md";

export interface ChipProps {
  tone?: ChipTone;
  size?: ChipSize;
  children: ReactNode;
  leadingIcon?: ReactNode;
  onClick?: MouseEventHandler<HTMLSpanElement>;
  outlined?: boolean;
  onClose?: () => void;
  className?: string;
}

export function Chip({
  tone = "default",
  size = "md",
  children,
  leadingIcon,
  onClick,
  outlined,
  onClose,
  className,
}: ChipProps) {
  return (
    <span
      className={cn("lex-chip", outlined && "lex-chip--outlined", className)}
      data-tone={tone === "default" ? undefined : tone}
      data-size={size}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {leadingIcon ? <span className="lex-chip__icon" aria-hidden="true">{leadingIcon}</span> : null}
      <span>{children}</span>
      {onClose ? (
        <button
          type="button"
          className="lex-chip__close"
          aria-label="Remove"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
        >×</button>
      ) : null}
    </span>
  );
}

const STATUS_TONE: Record<RecordStatus, ChipTone> = {
  draft: "default",
  pending: "tertiary",
  approved: "secondary",
  rejected: "error",
  live: "secondary",
  conflict: "error",
  deprecated: "default",
};

const STATUS_LABEL: Record<RecordStatus, string> = {
  draft: "Draft",
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  live: "Live",
  conflict: "Conflict",
  deprecated: "Deprecated",
};

export function StatusChip({ status, size = "sm" }: { status: RecordStatus; size?: ChipSize }) {
  return <Chip tone={STATUS_TONE[status]} size={size}>{STATUS_LABEL[status]}</Chip>;
}

export default Chip;
